import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { resetearDatosObjeto } from '../../services/api';

const SECCIONES = [
  {
    id: 'expediente',
    label: 'Expediente clinico',
    desc: 'Visitas medicas, recetas, diagnosticos'
  },
  {
    id: 'cartilla',
    label: 'Cartilla de salud',
    desc: 'Vacunas y desparasitaciones'
  },
  {
    id: 'peso',
    label: 'Historial de peso',
    desc: 'Todos los registros de peso'
  },
  {
    id: 'recordatorios',
    label: 'Recordatorios',
    desc: 'Rutinas, tratamientos, proximas, proveedores'
  }
];

export default function ResetearDatosModal({ show, onHide, objetos }) {
  // Steps: 'seleccionarObjeto' | 'ingresarPin' | 'seleccionarSecciones' | 'confirmar'
  const [paso, setPaso] = useState('seleccionarObjeto');
  const [objetoSeleccionado, setObjetoSeleccionado] = useState(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [seccionesSeleccionadas, setSeccionesSeleccionadas] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);

  function handleClose() {
    setPaso('seleccionarObjeto');
    setObjetoSeleccionado(null);
    setPin('');
    setPinError('');
    setSeccionesSeleccionadas([]);
    setConfirmText('');
    setSubmitting(false);
    setResultado(null);
    onHide();
  }

  function handleSelectObjeto(obj) {
    setObjetoSeleccionado(obj);
    setPaso('ingresarPin');
  }

  function handlePinSubmit(e) {
    e.preventDefault();
    if (!/^[A-Za-z0-9]{3}$/.test(pin)) {
      setPinError('PIN invalido (3 caracteres alfanumericos)');
      return;
    }
    setPinError('');
    setPaso('seleccionarSecciones');
  }

  function toggleSeccion(id) {
    setSeccionesSeleccionadas(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  }

  async function handleConfirmar() {
    setSubmitting(true);
    try {
      const resp = await resetearDatosObjeto(objetoSeleccionado.id, pin, seccionesSeleccionadas);
      if (resp.error) {
        setPinError(resp.error);
        if (resp.error === 'PIN incorrecto') {
          setPaso('ingresarPin');
          setPin('');
        }
        setSubmitting(false);
        return;
      }
      setResultado(resp.borrados);
    } catch (err) {
      setPinError('Error inesperado');
    } finally {
      setSubmitting(false);
    }
  }

  const emojiForType = (tipo) => {
    switch (tipo) {
      case 'mascota': return '🐾';
      case 'llavero': return '🔑';
      case 'coleccionable': return '⭐';
      default: return '📦';
    }
  };

  const seccionesLabels = seccionesSeleccionadas.map(id => SECCIONES.find(s => s.id === id)?.label);

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb' }}>
        <Modal.Title style={{ fontSize: '16px', fontWeight: '600' }}>
          {paso === 'seleccionarObjeto' && 'Resetear datos'}
          {paso === 'ingresarPin' && 'Verificar PIN'}
          {paso === 'seleccionarSecciones' && `Resetear datos de ${objetoSeleccionado?.nombre}`}
          {paso === 'confirmar' && 'Confirmar reseteo'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* PASO 1: Seleccionar mascota/objeto */}
        {paso === 'seleccionarObjeto' && (
          <div>
            <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
              Selecciona la mascota u objeto:
            </p>
            <div className="d-grid gap-2">
              {objetos?.map(obj => (
                <Button
                  key={obj.id}
                  variant="outline-secondary"
                  className="text-start d-flex align-items-center gap-2"
                  onClick={() => handleSelectObjeto(obj)}
                  style={{ padding: '12px 16px' }}
                >
                  <span style={{ fontSize: '20px' }}>{emojiForType(obj.tipo)}</span>
                  <span style={{ fontWeight: '500' }}>{obj.nombre}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* PASO 2: Ingresar PIN */}
        {paso === 'ingresarPin' && (
          <div>
            <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
              Ingresa tu PIN para continuar:
            </p>
            {pinError && <Alert variant="danger" className="py-2"><small>{pinError}</small></Alert>}
            <Form onSubmit={handlePinSubmit}>
              <Form.Control
                type="password"
                maxLength={3}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="PIN"
                autoFocus
                className="text-center"
                style={{ fontSize: '24px', letterSpacing: '8px', maxWidth: '160px', margin: '0 auto' }}
              />
              <div className="d-flex gap-2 justify-content-end mt-3">
                <Button variant="outline-secondary" size="sm" onClick={() => { setPaso('seleccionarObjeto'); setPin(''); setPinError(''); }}>
                  Atras
                </Button>
                <Button type="submit" variant="primary" size="sm" disabled={pin.length !== 3}>
                  Continuar
                </Button>
              </div>
            </Form>
          </div>
        )}

        {/* PASO 3: Seleccionar secciones */}
        {paso === 'seleccionarSecciones' && (
          <div>
            <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
              Selecciona que quieres borrar:
            </p>
            <div className="d-grid gap-2 mb-3">
              {SECCIONES.map(sec => (
                <div
                  key={sec.id}
                  onClick={() => toggleSeccion(sec.id)}
                  style={{
                    padding: '12px 16px',
                    border: `1px solid ${seccionesSeleccionadas.includes(sec.id) ? '#dc3545' : '#dee2e6'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: seccionesSeleccionadas.includes(sec.id) ? '#fff5f5' : '#fff',
                    transition: 'all 0.15s'
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="checkbox"
                      checked={seccionesSeleccionadas.includes(sec.id)}
                      onChange={() => toggleSeccion(sec.id)}
                      style={{ pointerEvents: 'none' }}
                    />
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>{sec.label}</div>
                      <div className="text-muted" style={{ fontSize: '12px' }}>{sec.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Alert variant="warning" className="py-2 mb-0">
              <small>Esta accion no se puede deshacer</small>
            </Alert>
            <div className="d-flex gap-2 justify-content-end mt-3">
              <Button variant="outline-secondary" size="sm" onClick={() => setPaso('ingresarPin')}>
                Atras
              </Button>
              <Button
                variant="danger"
                size="sm"
                disabled={seccionesSeleccionadas.length === 0}
                onClick={() => setPaso('confirmar')}
              >
                Resetear seleccionados
              </Button>
            </div>
          </div>
        )}

        {/* PASO 4: Confirmacion final */}
        {paso === 'confirmar' && !resultado && (
          <div>
            <p style={{ fontSize: '14px' }}>
              Vas a borrar de <strong>{objetoSeleccionado?.nombre}</strong>:
            </p>
            <ul style={{ fontSize: '14px' }}>
              {seccionesLabels.map((label, i) => (
                <li key={i}>{label}</li>
              ))}
            </ul>
            <Alert variant="danger" className="py-2">
              <small><strong>Esta accion NO se puede deshacer.</strong></small>
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '13px' }}>
                Escribe <strong>BORRAR</strong> para confirmar:
              </Form.Label>
              <Form.Control
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="BORRAR"
                autoFocus
              />
            </Form.Group>
            {pinError && <Alert variant="danger" className="py-2"><small>{pinError}</small></Alert>}
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="outline-secondary" size="sm" onClick={() => { setPaso('seleccionarSecciones'); setConfirmText(''); setPinError(''); }}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                size="sm"
                disabled={confirmText !== 'BORRAR' || submitting}
                onClick={handleConfirmar}
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-1" />
                    Eliminando...
                  </>
                ) : 'Confirmar'}
              </Button>
            </div>
          </div>
        )}

        {/* RESULTADO */}
        {resultado && (
          <div className="text-center py-3">
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
            <h6>Datos eliminados correctamente</h6>
            <p className="text-muted" style={{ fontSize: '13px' }}>
              Los datos seleccionados de <strong>{objetoSeleccionado?.nombre}</strong> han sido eliminados.
            </p>
            <Button variant="primary" size="sm" onClick={handleClose}>
              Cerrar
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
